import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, CheckCircle2, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { SubscriptionTier } from '@/types';
import { useOnboarding } from '../../context/OnboardingContext';

// ─── Template data ─────────────────────────────────────────────────────────────

interface TemplateTask {
  title: string;
  templateType: 'action' | 'maintenance';
  recurrence?: string;
}

interface TemplateEvent {
  title: string;
  recurrence: string;
}

interface Template {
  id: string;
  name: string;
  description: string;
  tasks: TemplateTask[];
  groceryCategories: string[];
  events: TemplateEvent[];
}

const TEMPLATES: Template[] = [
  {
    id: 'new-homeowner',
    name: 'New Homeowner',
    description: 'Maintenance reminders and essentials for your first home.',
    tasks: [
      { title: 'Replace HVAC filter', templateType: 'maintenance', recurrence: 'quarterly' },
      { title: 'Clean gutters', templateType: 'maintenance', recurrence: 'biannual' },
      { title: 'Test smoke detectors', templateType: 'maintenance', recurrence: 'monthly' },
      { title: 'Lawn care', templateType: 'maintenance', recurrence: 'weekly' },
    ],
    groceryCategories: ['Cleaning Supplies', 'Hardware', 'Pantry'],
    events: [{ title: 'Home inspection reminder', recurrence: 'quarterly' }],
  },
  {
    id: 'young-family',
    name: 'Young Family',
    description: 'School runs, appointments, and family rhythms.',
    tasks: [
      { title: 'School pickup', templateType: 'action', recurrence: 'daily' },
      { title: 'Pediatrician appointment', templateType: 'action' },
      { title: 'Kids activity reminder', templateType: 'action', recurrence: 'weekly' },
    ],
    groceryCategories: ['Baby & Kids', 'School Supplies', 'Snacks'],
    events: [{ title: 'Weekly family dinner night', recurrence: 'weekly' }],
  },
  {
    id: 'solo-dweller',
    name: 'Solo Dweller',
    description: 'Personal routines, budgeting, and self-care essentials.',
    tasks: [
      { title: 'Weekly grocery run', templateType: 'action', recurrence: 'weekly' },
      { title: 'Monthly bill review', templateType: 'action', recurrence: 'monthly' },
      { title: 'Gym day', templateType: 'action', recurrence: 'weekly' },
    ],
    groceryCategories: ['Produce', 'Frozen', 'Personal Care'],
    events: [{ title: 'Monthly budget check-in', recurrence: 'monthly' }],
  },
  {
    id: 'couple',
    name: 'Couple',
    description: 'Shared finances, date nights, and household basics.',
    tasks: [
      { title: 'Plan date night', templateType: 'action', recurrence: 'weekly' },
      { title: 'Joint bill tracker', templateType: 'action', recurrence: 'monthly' },
      { title: 'Grocery run', templateType: 'action', recurrence: 'weekly' },
    ],
    groceryCategories: ['Fresh Produce', 'Household', 'Beverages'],
    events: [{ title: 'Weekly check-in', recurrence: 'weekly' }],
  },
];

// ─── Apply template via batch write ───────────────────────────────────────────

async function applyTemplate(householdId: string, template: Template): Promise<void> {
  const batch = writeBatch(db);
  const now = serverTimestamp();

  // Tasks (action + maintenance)
  for (const task of template.tasks) {
    const taskRef = doc(collection(db, 'households', householdId, 'tasks'));
    batch.set(taskRef, {
      title: task.title,
      templateType: task.templateType,
      recurrence: task.recurrence ?? null,
      status: 'pending',
      scope: 'household',
      fromTemplate: template.id,
      createdAt: now,
    });
  }

  // Calendar events
  for (const event of template.events) {
    const eventRef = doc(collection(db, 'households', householdId, 'events'));
    batch.set(eventRef, {
      title: event.title,
      recurrence: event.recurrence,
      scope: 'household',
      fromTemplate: template.id,
      createdAt: now,
    });
  }

  // Grocery categories — merge into existing settings doc
  const catRef = doc(db, 'households', householdId, 'settings', 'customGroceryCategories');
  const categoryColors = ['#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ec4899', '#8b5cf6'];
  const catMap: Record<string, string> = {};
  template.groceryCategories.forEach((name, i) => {
    catMap[name] = categoryColors[i % categoryColors.length];
  });
  batch.set(catRef, { categories: catMap }, { merge: true });

  await batch.commit();
}

// ─── Component ────────────────────────────────────────────────────────────────

interface TemplateSelectorProps {
  householdId: string;
  userTier?: string;
  onApplied: () => void;
  onSkip: () => void;
}

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  householdId,
  userTier,
  onApplied,
  onSkip,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setTemplateApplied } = useOnboarding();
  const [selected, setSelected] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPaid = userTier && userTier !== SubscriptionTier.FREE;
  // Solo Dweller is available on the free tier to give new users an immediate value moment
  const FREE_TEMPLATES = new Set(['solo-dweller']);

  const handleApply = async () => {
    if (!selected) return;
    const template = TEMPLATES.find((t) => t.id === selected);
    if (!template) return;

    setApplying(true);
    setError(null);
    try {
      await applyTemplate(householdId, template);
      await setTemplateApplied(template.id);
      onApplied();
    } catch (e: any) {
      setError(e.message ?? 'Failed to apply template');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {TEMPLATES.map((tmpl) => {
          const locked = !isPaid && !FREE_TEMPLATES.has(tmpl.id);
          const isSelected = selected === tmpl.id;

          return (
            <button
              key={tmpl.id}
              disabled={locked}
              onClick={() => !locked && setSelected(isSelected ? null : tmpl.id)}
              className={`relative text-left p-3 rounded-xl border transition-all ${
                locked
                  ? 'border-slate-700 bg-slate-800/50 opacity-60 cursor-default'
                  : isSelected
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-slate-700 bg-slate-800 hover:border-slate-600'
              }`}
            >
              {locked && (
                <div className="absolute top-2 right-2">
                  <Lock className="w-3 h-3 text-slate-500" />
                </div>
              )}
              {isSelected && !locked && (
                <div className="absolute top-2 right-2">
                  <CheckCircle2 className="w-3 h-3 text-primary-400" />
                </div>
              )}
              <p className="text-white text-xs font-semibold mb-0.5">
                {t(`onboarding.template.${tmpl.id.replaceAll('-', '_')}_name`, tmpl.name)}
              </p>
              <p className="text-slate-400 text-2xs leading-relaxed">
                {t(`onboarding.template.${tmpl.id.replaceAll('-', '_')}_desc`, tmpl.description)}
              </p>
              {locked && (
                <p className="text-primary-400 text-2xs mt-1 font-semibold">
                  {t('onboarding.template.upgrade_required', 'Upgrade to unlock')}
                </p>
              )}
            </button>
          );
        })}
      </div>

      {!isPaid && (
        <button
          onClick={() => navigate('/upgrade')}
          className="w-full text-center text-xs text-primary-400 hover:text-primary-300 transition-colors py-1"
        >
          {t('onboarding.template.upgrade_cta', 'Upgrade to Basic to unlock templates')} →
        </button>
      )}

      {error && (
        <p className="text-red-400 text-xs text-center">{error}</p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={onSkip}
          className="flex-1 py-2.5 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:border-slate-600 text-sm font-semibold transition-colors"
        >
          {t('onboarding.template.skip', 'Skip for now')}
        </button>
        {isPaid && selected && (
          <button
            onClick={handleApply}
            disabled={applying}
            className="flex-1 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors"
          >
            {applying
              ? t('common.saving', 'Saving…')
              : <>{t('onboarding.template.apply', 'Apply template')} <ArrowRight className="w-3.5 h-3.5" /></>
            }
          </button>
        )}
      </div>
    </div>
  );
};
